/**
 * Component to load the Table containing all of the Returns
 * within the main Returns-Dashboard.
 *
 * @component
 */

import { Badge,Button,Table,Tag,Tooltip } from 'antd'
import { InfoCircleTwoTone,CreditCardOutlined,MailOutlined,CarOutlined,CheckCircleTwoTone,CloseSquareOutlined,LoadingOutlined,ShrinkOutlined } from '@ant-design/icons'
import moment from 'moment'
import React from 'react'
import { IconTray } from '../../../../components/CustomReturnsIcons'
import { SubscriptionContext } from "../../../../contexts/SubscriptionContext"
import '../../../../styles/App.css'
import ReturnManagement from './ReturnManagement'
import { trackingVitally } from '../../../../helpers/vitally'

const typeColor = {
  return: {
    color: '#004085',
    backgroundColor: '##81ecec',
    borderColor: '#b8daff'
  },
  giftReturn: {
    width: '52px',
    textAlign: 'center',
    color: '#856404',
    backgroundColor: '#fdcb6e',
    borderColor: '#e17055'
  },
  warranty: {
    textAlign: 'center',
    color: '#856404',
    backgroundColor: '#fdcb6e',
    borderColor: '#e17055'
  },
};

class ReturnsTable extends React.Component {
  static contextType = SubscriptionContext;

  constructor(props) {
    super(props);
    this.state = {
      drawerVisible: false,
      returnId: null,
      currentPage: 1,
      showIconToolTip: false,
      modalRecord: null,
      isManageButtonLoading: false
    };
    this.resolutionOptions = {
      refundToOriginal: 'Refund',
      refundToExchange: 'Exchange',
      refundToCredit: 'Store credit',
      advancedExchange: 'Collection Exchange',
      multiple: 'Multiple',
      refundToCustom1: 'Custom 1',
      refundToCustom2: 'Custom 2',
      refundToCustom3: 'Custom 3',
      storeWideExchange: 'Store-Wide Exchange',
      warranty: "Warranty",
    };

    this.typeReturn = {
      return: "Return",
      giftReturn: "Gift",
      warranty: "Warranty",
    };

    this.returnMethodOptions = {
      methodLabelCreation: 'Prepaid Label (Auto)',
      methodLabelManual: 'Prepaid Label (Manual)',
      methodCustomerPaidLabel: 'Customer Paid Label',
      methodCustomerChoice: 'Customer Responsible',
      methodInStore: 'In-Store Return',
      warranty: 'Warranty',

    };

    this.statusColorMap = {
      Pending: 'orange',
      Approved: 'cyan',
      Rejected: 'magenta',
      'In Progress': 'blue',
    };

    this.columns = [
      {
        title:  <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="return-dashboard"> Return <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'returnNumber',
        key: 'returnNumber',
        customKey: 'Return',
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('returnNumber')
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="status-dashboard"> Status <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'returnStatus',
        key: 'returnStatus',
        customKey: 'Status',
        render: (text) => {
          const color = this.statusColorMap[text] || 'blue';
          return (
            <span>
              <Tag color={color} key={text}>
                {text}
              </Tag>
            </span>
          );
        },
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('returnStatus')
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="journey-dashboard"> Journey <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'returnStep',
        key: 'returnStep',
        customKey: 'Journey',
        render: (text, record) => (
          <Tooltip 
            title={
              <>
                {record.labelStatus && record.labelStatus !== 'ERROR'
                  ? <>{`Carrier: ${this.getCarrierName(record.labelRateProvider)}`}<br/></>
                  : null}
                {this.context.featureAutoTracking ? null : `Want carrier auto-tracking? Upgrade your plan to Enterprise.`}
                {!(this.context.featureAutoTracking) 
                  ? null 
                  : (
                    <>
                      {`Order status: ${record.latestReturnStatusSubtype ? this.cleanOrderStatus(record.latestReturnStatusSubtype) : 'Unavailable'}`}
                      {record.statusSubtypeUpdated ? ` as of ${this.cleanISODate(record.statusSubtypeUpdated)}` : ''}
                      <br/>
                    </>
                  )
                }
                {!(this.context.featureAutoTracking) || (record.returnStatus === 'Resolved' || record.returnStatus === 'Rejected' || record.returnStatus === 'Received') 
                  ? null
                  : `Estimated Delivery: ${this.cleanISODate(record.estimatedDelivery)}`}
              </>
            } 
            placement={'topLeft'}
          >
            <div
              style={{
                fontSize: '20px',
                display: 'flex',
                justifyContent: 'left',
              }}
            >
              {this.getIconType(record)}
            </div>
          </Tooltip>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('journey')
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="label-dashboard"> Label <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'errors',
        key: 'errors',
        customKey: 'Label',
        render: (text, record) => (
          <React.Fragment>
            {record.labelStatus === 'ERROR' ? (
              <div className="u-width--100" style={{ textAlign: 'center' }}>
                <Tooltip
                  placement="top"
                  title={
                    record.labelErrorTransactionSource
                      ? record.labelErrorTransactionSource +
                        ': ' +
                        record.labelErrorTransactionText +
                        ' (label could not be created & sent to customer)'
                      : record.labelErrorTransactionText +
                        ' (label could not be created & sent to customer).'
                  }
                >
                  <Badge status="error" />
                </Tooltip>
              </div>
            ) : !record.labelStatus ? (
              <div className="u-width--100" style={{ textAlign: 'center' }}>
                <Tooltip
                  placement="top"
                  title={'No label generated for this return method.'}
                >
                  <Badge status="default" />
                </Tooltip>
              </div>
            ) : (
              <div className="u-width--100" style={{ textAlign: 'center' }}>
                <Tooltip
                  placement="top"
                  title={'Shipping label has been successfully created.'}
                >
                  <Badge status="success" />
                </Tooltip>
              </div>
            )
          
            }
          </React.Fragment>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('labelStatus')
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="type-dashboard"> Type <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'type',
        key: 'type',
        customKey: 'Type',
        render: (text, record) => {
          // Warranty is tracked separately from `type` (Return.returnFlow) so it
          // doesn't collide with gift-return semantics on `type`. Prefer it here.
          const typeKey = record.returnFlow === 'warranty' ? 'warranty' : text;
          return (
            <span>
              <Tag style={typeColor[typeKey]} key={typeKey}>{this.typeReturn[typeKey]}</Tag>
            </span>
          );
        },
        onHeaderCell: () => {
          return {
            onClick: () => this.props.handleSorter('type')
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="order-dashboard"> Order <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'orderName',
        key: 'orderName',
        customKey: 'Order',
        render: (text, record) => {
          if (record.orderName) {
            return (
              <React.Fragment>
                <span>{record.orderName}</span>
              </React.Fragment>
            );
          } else {
            return <span>{record.orderNumber}</span>;
          }
        },
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('orderName');
            },
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="customer-dashboard"> Customer <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'customerFirstName',
        key: 'customerFirstName',
        customKey: 'Customer',
        render: (text, record) => (
          <span style={{ textTransform: 'capitalize' }}>
            {record.customerFirstName + ' ' + record.customerLastName}
          </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('customerFirstName');
            },
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="order-country-dashboard"> Order Country <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'shippingCountry',
        key: 'shippingCountry',
        customKey: 'Order Country',
        render: (text, record) => (
          <span>
            {record.shippingCountry || 'N/A'}
          </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('shippingCountry');
            },
          };
        },
        className: 'filter',
      },
      {
        title:  <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="resolution-dashboard"> Resolution <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'resolution',
        key: 'resolution',
        customKey: 'Resolution',
        render: (text, record) => {
          const isCustomResolution =
            record.resolution && record.resolution.includes('refundToCustom');
          if (isCustomResolution) {
            const customResolutionLabel = record.resolutionLabel;
            return (
              <Tooltip placement="top" title={customResolutionLabel}>
                <span>{this.resolutionOptions[record.resolution]}</span>
              </Tooltip>
            );
          } else {
            return record.resolution
              ? this.resolutionOptions[text]
              : 'Multiple';
          }
        },
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('resolution');
            },
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="return-reason-dashboard"> Return Reason <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'returnReason',
        key: 'returnReason',
        customKey: 'Return Reason',
        render: (text, record) => {
          return <span>{record.returnReason}</span>;
        },
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="return-method-dashboard"> Return Method <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'shippingMethod',
        key: 'shippingMethod',
        customKey: 'Return Method',
        render: (text, record) => {
          return (
            <span>
              {this.returnMethodOptions[record.shippingMethod] || record.shippingMethod || ' '}
            </span>
          );
        },
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('shippingMethod');
            },
          };
        },
        className: 'filter',
      },
      {
        title:  'Return Cost',
        dataIndex: 'returnCost',
        key: 'returnCost',
        customKey: 'Return Cost',
        render: (text, record) => {
          const currency = record.presentmentCurrency || record.currency || 'USD';
          const calculatedCost = parseFloat(record.calculatedReturnCost);
          
          return (
            <span>
              {`${currency} ${calculatedCost}`}
            </span>
          );
        },
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="shipping-carrier-dashboard"> Shipping Carrier <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'labelRateProvider',
        key: 'labelRateProvider',
        customKey: 'Shipping Carrier',
        render: (text, record) => {
          if (!record.labelRateProvider) {
            return <span> </span>;
          }
          return (
            <span>
              {this.getCarrierName(record.labelRateProvider)}
            </span>
          );
        },
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('labelRateProvider');
            },
          };
        },
        className: 'filter',
      },
      {
        title:  <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="request-date-dashboard"> Request date <ShrinkOutlined rotate={135} /> </span>,
        dataIndex: 'requestDate',
        key: 'requestDate',
        customKey: 'Request date',
        render: (text, record) => (
          <span> {moment(record.requestDate).format('MMM DD, YYYY')} </span>
        ),
        onHeaderCell: () => {
          return {
            onClick: () => {
              this.props.handleSorter('createdAt');
            },
          };
        },
        className: 'filter',
      },
      {
        title: <span style={{display: 'flex', alignItems: 'center', gap: '4px'}} data-intercom-target="action-dashboard"> Action </span>,
        dataIndex: 'action',
        key: 'action',
        customKey: 'Action',
        render: (text, record) => {
          return (
            <Button
              type="default"
              loading={this.state.isManageButtonLoading}
              size="small"
              onClick={() => {
                this.setState({ drawerVisible: true, returnId: record.id }),
                trackingVitally('rr_managereturnorder_clicked');
              }}
              data-intercom-target="manage-button-dashboard"
            >
              Manage
            </Button>
          );
        },
      },
    ];
  }

  setManageButtonLoading = (isLoading) => {
    this.setState({isManageButtonLoading: isLoading})
  }

  getIconType = (record) => {
    const currentStep = this.getCurrentStep(record);
    switch (currentStep) {
    case -1:
      return <InfoCircleTwoTone twoToneColor={this.getIconColor(record)} style={{ fontSize: '20px' }} />;
    case 0:
      return <CreditCardOutlined style={{ fontSize: '20px', color: this.getIconColor(record) }} />;
    case 1:
      return <MailOutlined style={{ fontSize: '20px', color: this.getIconColor(record) }} />;
    case 2:
      return <CarOutlined style={{ fontSize: '20px', color: this.getIconColor(record) }} />;
    case 3:
      return <CheckCircleTwoTone twoToneColor={this.getIconColor(record)} style={{ fontSize: '20px' }} />;
    case 4:
      return <CloseSquareOutlined style={{ fontSize: '20px', color: this.getIconColor(record) }} />;
    default:
      return <LoadingOutlined style={{ fontSize: '20px', color: this.getIconColor(record) }} />;
    }
  };

getIconColor = (record) => {
  const currentStep = this.getCurrentStep(record);
  switch (currentStep) {
  case -1:
    return '#CCAC00';
  case 0:
    return '#eb2f96';
  case 1:
    return '#ff6600';
  case 2:
    return '#2db7f5';
  case 3:
    return '#52c41a';
  case 4:
    return 'red';
  default:
    return '#67C2C2';
  }
}

  cleanOrderStatus = (orderStatus) => {
    if (!orderStatus) {
      return "Unavailable";
    }
    const words = orderStatus.split('_');
    const cleanedStatus = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
    return cleanedStatus.join(' ');
  }

  cleanISODate = (date) => {
    if (!date) {
      return "Unavailable";
    }
    return new Date(date)
      .toISOString()
      .replace('T', ' ')
      .replace(/\.\d{3}/, '')
      .replace('Z', ' (UTC)');
  }

  onSelectChange = (selectedRowKeys, selectedRows) => {
    this.props.setSelectedRows(selectedRowKeys, selectedRows);
  };

  triggerPagination = (page) => {
    const paginationObject = {
      current: page,
      pageSize: 10,
    };
    this.props.paginatePages(paginationObject);
    this.setState({
      currentPage: paginationObject.current,
    });
  };

  hideDrawer = () => {
    this.setState({
      drawerVisible: false,
    });
    if (!this.props.searchValue) {
      this.props.resetSearchResults(this.state.currentPage);
    } else if (this.props.searchValue) {
      this.props.searchTerm(this.props.searchValue);
    }
  };

  getCurrentStep = (returnObject) => {
    const statusToStepMap = {
      "Pending": 0,
      "Approved": 1,
      "Shipped": 2,
      "In Transit": 2,
      "Received": 3,
      "Resolved": 3,
      "Rejected": 4,
    };
    const orderStatus = returnObject.returnStatus;
    if (orderStatus == null || orderStatus == '') {
      return -1;
    }
    return statusToStepMap[orderStatus];
  }

  getCarrierName(carrierCode) {
    if (!carrierCode) return 'could not be retrieved.';

    const carrierNameMappings = {
      'stamps_com': 'Stamps.com',
    };

    const normalizedCarrierCode = carrierCode.toLowerCase();
    if (carrierNameMappings.hasOwnProperty(normalizedCarrierCode)) {
      return carrierNameMappings[normalizedCarrierCode];
    }
  
    // List of common carrier acronyms
    const carriers = [
      'UPS', 'USPS', 'FEDEX', 'DHL', 'EMS', 'TNT', 'DPD', 'CANADA POST',
      'ROYAL MAIL', 'AUSPOST', 'PUROLATOR', 'GLS', 'HERMES', 'LASERSHIP',
      'ONTRAC', 'FASTWAY', 'SENDLE', 'ASENDIA', 'ARAMEX', 'YAMATO', 'SEUR',
      'CHRONOPOST', 'COLISSIMO', 'POSTNL', 'BPOST', 'CORREOS', 'LA POSTE',
      'AUSTRALIA POST'
    ];
  
    let carrierName = carrierCode.replace(/_/g, ' ');
  
    const carrierAcronym = carrierName.split(' ')[0].toUpperCase();
    if (carriers.includes(carrierAcronym)) {
      return carrierAcronym;
    }
  
    carrierName = carrierName
      .split(' ')
      .map(word => {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  
    return carrierName;
  }

  render() {
    // if (isEmpty(this.props.returns)) {
    //   return <Table columns={this.emptyColumns} dataSource={this.data} pagination={false} />
    // }
    const processedReturns = this.props.returns.map(returnObj => {
      const returnProducts = this.props.productsOfReturns.filter(
        product => product.ReturnId === returnObj.id
      );
      
      const returnReason = returnProducts.find(product => product.returnReason && product.returnReason.trim() !== '')?.returnReason || '';
      
      return {
        ...returnObj,
        returnReason
      };
    });

    const rowSelection = {
      onChange: this.onSelectChange,
    };

    const { customColumns } = this.props;

    const filteredColumns = this.columns.filter((col) => {
      if (!customColumns) return true;
      const isShown = customColumns[col.customKey];
      // We hide any missing column
      return typeof isShown !== 'undefined' ? isShown : false;
    });

    return (
      <React.Fragment>
        <div>
          <div style={{ overflowX: 'auto' }}>
            <Table
              loading={this.props.loading}
              rowSelection={rowSelection}
              columns={filteredColumns}
              rowKey="returnNumber"
              dataSource={processedReturns}
              pagination={{
                total: this.props.returnsCount,
                current: this.props.currentPage,
                pageSize: 10,
                showSizeChanger: false,
                onChange: (page) => this.triggerPagination(page),
              }}
              locale={{
                emptyText: (
                  <div className="flex-row flex-middle-xxs flex-center-xxs">
                    <div className="flex-col-xxs-12">
                      <IconTray size="md" />
                    </div>
                    <div>
                      <p className="TextBody TextBody--xxxs">No Data</p>
                    </div>
                  </div>
                ),
              }}
            />
            {this.state.drawerVisible ? (
              <ReturnManagement
                setManageButtonLoading={this.setManageButtonLoading.bind(this)}
                returnId={this.state.returnId}
                drawerVisible={this.state.drawerVisible}
                resolutionOptions={this.resolutionOptions}
                hideDrawer={this.hideDrawer.bind(this)}
                updateData={this.props.updateData.bind(this)}
                setVisibilityOfResetSearchButton={this.props.setVisibilityOfResetSearchButton.bind(
                  this
                )}
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default ReturnsTable;
